import { useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import Link from 'next/link'
import { useSelector } from 'react-redux'
import { useQuery } from '@tanstack/react-query'
import {
  CalendarBlank,
  CaretLeft,
  CaretRight,
  MapPin,
  SmileyWink,
  VideoCamera
} from 'phosphor-react'
import dynamic from 'next/dynamic'

import { Text } from 'components/v2/Text'
import { Radio } from 'components/v2/Radio'
import { Select } from 'components/v2/Select'
import { Button } from 'components/v2/Button'
import { Row, Column } from 'components/v2/Grid'
import { NextImage } from 'components/v2/NextImage'
import { BookmarkButton } from 'components/v2/Favorite'
import { Calendar } from 'components/v2/CalendarCartVariant'
import { sendSelectItemEvent } from 'components/shared/DataLayer'
import Evaluation from 'components/Search/Results/Card/Intro/evaluation'

import { useUser } from 'providers/UserProvider'
import { useTimezone } from 'providers/TimezoneProvider'

import {
  calculateNewDates,
  getDataContractId,
  getDataFromScheduleResponse,
  getYouTubeVideoId,
  openMap
} from 'utils/helpers'
import { getScheduleByProfessionalSlug } from 'services/professionalService'
import { formatAppointmentDates, getDistance } from 'utils/formatters'

// Página de busca do psi

const PsychoPrice = dynamic(() => import('components/v2/PsychoPrice/PsychoPrice'), { ssr: false })

const PsychoCard = ({ psychologist, isMatchMaker, handleMultipleScheduling }) => {
  const { user, isPresencialConsultationEnabled, location: userLocation } = useUser()
  const { timezone } = useTimezone()
  const { filters } = useSelector(state => ({
    filters: state.filters
  }))

  const userContractId = user?.contract?.id || getDataContractId()?.integration?.contract?.id

  const currentSchedule = {
    coupon: '',
    psico_name: psychologist.name,
    psico_id: psychologist.id,
    is_teleconf: true,
    prof_address_slug: psychologist.prof_address_slug,
    social_thumb: psychologist.social_thumb,
    crp: psychologist.prof_register,
    evaluations: psychologist.rating ? psychologist.rating.total : 0,
    slug: psychologist.slug,
    type: psychologist.type,
    totalComments: psychologist.evaluations.length,
    timezone: timezone,
    timestamp_slot: psychologist.distance ? psychologist.distance.schedule_id : ''
  }

  const [clinicId, setClinicId] = useState(() => {
    if (filters?.consultation === 'presencial') {
      return psychologist.schedules.find(schedule => schedule.location !== null)?.id
    }

    return psychologist.schedules.find(schedule => schedule.location === null)?.id
  })
  const [isCalendarLoading, setIsCalendarLoading] = useState(false)
  const [startDate, setStartDate] = useState(moment().format('YYYYMMDD'))
  const [endDate, setEndDate] = useState(
    moment()
      .add(4, 'days')
      .format('YYYYMMDD')
  )
  const [distance, setDistance] = useState(null)
  const [selectedDate, setSelectedDate] = useState(null)
  const [locationSelected, setLocationSelected] = useState(filters?.consultation || 'online')

  const hasSymptom = useMemo(() => {
    return psychologist.symptoms.find(symptom => symptom === filters.symptoms[0])
  }, [psychologist, filters])

  useEffect(() => {
    if (filters?.startDate !== moment().format('YYYY-MM-DD')) {
      setStartDate(filters?.startDate)
      setEndDate(filters?.endDate)
    }
  }, [filters?.startDate, filters?.endDate])

  const changeConsultationType = value => {
    if (value === 'presencial') {
      const location = psychologist.schedules.find(schedule => schedule.location !== null)
      setDistance(
        getDistance(userLocation, {
          lat: location?.location?.latitude,
          lng: location?.location?.longitude
        }).toFixed(1)
      )
      setClinicId(location?.id)
    } else {
      setClinicId(
        psychologist.schedules.find(schedule => schedule.location === null)?.id ||
          psychologist.schedules[0].id
      )
    }
    setLocationSelected(value)
  }

  const { data: schedules } = useQuery({
    queryKey: [
      'getSchedule',
      psychologist.slug,
      startDate,
      endDate,
      clinicId,
      userContractId,
      timezone
    ],
    queryFn: () =>
      getScheduleByProfessionalSlug(
        psychologist.slug,
        startDate,
        endDate,
        clinicId,
        null,
        userContractId,
        filters?.consultation,
        timezone
      ),
    select: data => {
      const schedules = getDataFromScheduleResponse(data)
      const appointments = formatAppointmentDates(schedules.dates, schedules.schedule)

      return {
        schedule: schedules.schedule,
        appointments,
        price: data.data.content.price,
        contract_price: data.data.content.contract_price
      }
    },
    staleTime: 1000 * 60 * 5 // 5 minutes
  })

  const previousDay = moment(startDate)
    .subtract(1, 'day')
    .isBefore(moment(), 'day')

  const lastDay = moment(endDate).isSameOrAfter(
    moment()
      .add(3, 'M')
      .format('YYYYMMDD')
  )

  const handleNextWeek = () => {
    const { newStartDate, newEndDate } = calculateNewDates(startDate, 5)

    setStartDate(newStartDate)
    setEndDate(newEndDate)
  }

  const handlePreviousWeek = () => {
    const isFirstDay = moment(startDate)
      .subtract(5, 'days')
      .isBefore(moment(), 'day')

    const daysToSubtract = isFirstDay ? 0 : 5

    const { newStartDate, newEndDate } = calculateNewDates(startDate, -daysToSubtract)

    setStartDate(newStartDate)
    setEndDate(newEndDate)
  }

  const sendDatalayer = ({ dataFrom }) => {
    const professionalDataForDataLayer = [
      {
        item_name: psychologist?.name,
        item_id: psychologist?.id,
        price: psychologist?.prices_teleconf?.max,
        from: dataFrom,
        variant: 'PRINCIPAL'
      }
    ]
    sendSelectItemEvent(professionalDataForDataLayer)
  }

  const deltaJoinedText = ({ years: deltaYears, months: deltaMonths }) => {
    if (deltaYears === 0 && deltaMonths <= 1) return '1 mês'

    const timeStrings = []

    deltaYears && timeStrings.push(`${deltaYears} ano${deltaYears > 1 ? 's' : ''}`)
    deltaMonths && timeStrings.push(`${deltaMonths} ${deltaMonths > 1 ? 'meses' : 'mês'}`)

    return timeStrings.join(', ')
  }

  return (
    <Row
      width='100%'
      px={['0px', '16px']}
      maxWidth='100%'
      key={psychologist?.id}
      itemScope
      itemType='http://schema.org/Physician'
      mb={['8px', '8px', '16px']}
      padding={isMatchMaker ? '8px' : ['16px', '16px', '0px']}
    >
      <Column width='100%' flex='0 0 100%' maxWidth='100%'>
        <Row
          width='100%'
          maxWidth='100%'
          borderRadius='4px'
          background='#fff'
          minHeight='320px'
          padding='16px'
          border='1px solid #E7EAF4'
          flexDirection={['column', 'column', 'row']}
          gridGap='16px'
        >
          <Column flex='1' maxWidth='100%' width='100%'>
            <Row width='100%' maxWidth='100%'>
              <Column
                width={['40%', '40%', '20%']}
                justifyContent='flex-start'
                alignItems='center'
                gridGap='4px'
              >
                <NextImage
                  src={psychologist.social_thumb || '/static/images/psicologo-sem-foto.png'}
                  alt={psychologist.name}
                  width={100}
                  height={100}
                  borderRadius='50%'
                  itemProp='image'
                />
                <PsychoPrice
                  originalPrice={schedules?.price?.value}
                  contractPrice={schedules?.contract_price?.value}
                />
                <Evaluation psycho={psychologist} numbertotal={psychologist?.rating?.total} />
                <Text fontSize='14px' textAlign='center' fontWeight='400'>
                  50 min
                </Text>
              </Column>
              <Column
                width={['60%', '60%', '80%']}
                alignItems='flex-start'
                justifyContent='flex-start'
                gridGap='12px'
              >
                <Row
                  width='100%'
                  maxWidth='100%'
                  alignItems='center'
                  justifyContent='space-between'
                >
                  <Row gridGap='8px' alignItems='center'>
                    <Link href={`/psicologo/${psychologist?.slug}`} passHref>
                      <Text
                        fontSize={
                          psychologist.name.length > 20
                            ? ['16px', '16px', '24px']
                            : ['20px', '20px', '28px']
                        }
                        fontWeight='600'
                        style={{ cursor: 'pointer' }}
                        textAlign='left'
                        onClick={() => sendDatalayer({ dataFrom: 'psychologist-name' })}
                      >
                        {psychologist.name}
                      </Text>
                    </Link>
                    {psychologist.number_of_consultations >= 10 && (
                      <NextImage
                        src='/static/images/verified.png'
                        alt='Psicólogo Verificado'
                        width={14}
                        height={14}
                      />
                    )}
                  </Row>
                  <BookmarkButton
                    professionalId={psychologist?.id}
                    professionalSlug={psychologist?.slug}
                    isLiked={psychologist?.is_liked}
                  />
                </Row>
                <Text fontSize='16px' fontWeight='600'>
                  CRP {psychologist.prof_register}
                </Text>
                {filters.symptoms.length > 0 && hasSymptom && (
                  <Row
                    flexDirection={['column', 'column', 'row']}
                    flexWrap='wrap'
                    alignItems='center'
                    justifyContent='flex-start'
                    gridGap='4px'
                  >
                    {hasSymptom && (
                      <Column
                        color='#293450'
                        p='4px 8px'
                        borderRadius='8px'
                        border='1px solid'
                        borderColor='#E7EAF4'
                        fontSize='14px'
                        lineHeight='24px'
                        mr='4px'
                      >
                        {filters.symptoms[0]}
                      </Column>
                    )}

                    {psychologist.symptoms.length > 1 && (
                      <Link href={`/psicologo/${psychologist?.slug}`} passHref prefetch>
                        <Text
                          color='#FF7708'
                          variant='small'
                          fontWeight='600'
                          cursor='pointer'
                          onClick={() => sendDatalayer({ dataFrom: 'psychologist-symptoms' })}
                        >
                          {hasSymptom
                            ? `+${psychologist.symptoms.length - 1} especialidades`
                            : `${psychologist.symptoms.length} especialidades`}
                        </Text>
                      </Link>
                    )}
                  </Row>
                )}
                {!psychologist.video_url ? (
                  <Column
                    gridGap='8px'
                    width='100%'
                    maxWidth='100%'
                    alignItems='flex-start'
                    justifyContent='flex-start'
                  >
                    <Text fontSize='12px' fontWeight='600'>
                      Sobre mim
                    </Text>
                    <Text fontSize='14px' fontWeight='400' textAlign='left'>
                      {psychologist?.mini_cv?.length > 254
                        ? psychologist?.mini_cv?.slice(0, 254) + '...'
                        : psychologist?.mini_cv}
                    </Text>
                  </Column>
                ) : (
                  <Row width='100%' maxWidth='100%' display={['none', 'none', 'flex']}>
                    <iframe
                      width='100%'
                      height='240'
                      src={`https://www.youtube.com/embed/${getYouTubeVideoId(
                        psychologist?.video_url
                      )}`}
                      title='YouTube video player'
                      style={{ border: 'none' }}
                      allowFullScreen
                    />
                  </Row>
                )}
                <Link href={`/psicologo/${psychologist?.slug}`} passHref>
                  <Text
                    fontSize='14px'
                    fontWeight='600'
                    color='primary'
                    variant='small'
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => sendDatalayer({ dataFrom: 'about-psychologist' })}
                  >
                    Ver perfil completo
                  </Text>
                </Link>
                {psychologist.languages.length !== 0 && (
                  <Row fontSize='14px' fontWeight='600' color='#999999' gridGap='4px'>
                    Atendimento também em{' '}
                    {psychologist.languages.map((language, index) => (
                      <Text key={index} fontWeight='300' color='#999999' fontSize='14px'>
                        {language}
                        {index !== psychologist.languages.length - 1 && '/ '}
                      </Text>
                    ))}
                  </Row>
                )}
                <Row width='100%' maxWidth='100%' alignItems='center' gridGap='64px'>
                  {psychologist.number_of_consultations >= 10 ? (
                    <>
                      <Row>
                        <Text color='primary' fontSize='14px' fontWeight='600' textAlign='left'>
                          <Text color='primary' fontSize='14px' fontWeight='600'>
                            <CalendarBlank size={18} /> {deltaJoinedText(psychologist.delta_joined)}
                          </Text>
                          <Text color='black' fontSize='14px' fontWeight='400'>
                            na Vittude
                          </Text>
                        </Text>
                      </Row>
                      <Row>
                        <Text color='primary' fontSize='14px' fontWeight='600' textAlign='left'>
                          <SmileyWink size={18} /> {psychologist.number_of_consultations} consultas{' '}
                          <br />{' '}
                          <Text color='black' fontSize='14px' fontWeight='400'>
                            realizadas
                          </Text>
                        </Text>
                      </Row>
                    </>
                  ) : (
                    <Row>
                      <Text color='primary' fontSize='14px' fontWeight='600' textAlign='left'>
                        <Text color='primary' fontSize='14px' fontWeight='600'>
                          Novo
                        </Text>
                        <Text color='black' fontSize='14px' fontWeight='400'>
                          na Vittude
                        </Text>
                      </Text>
                    </Row>
                  )}
                </Row>
              </Column>
            </Row>
          </Column>
          <Column
            width='100%'
            maxWidth={['100%', '100%', '380px']}
            px={['0px', '0px', '16px']}
            mt={['8px', '8px', '0px']}
          >
            {isPresencialConsultationEnabled && (
              <Row my='10px' justifyContent='space-around'>
                <Radio
                  initial={true}
                  checked={locationSelected !== 'presencial'}
                  disabled={!psychologist.schedules.some(schedule => schedule.location === null)}
                  label='Online'
                  name={psychologist?.id}
                  onClick={() => changeConsultationType('online')}
                  icon={<VideoCamera fontSize={16} color='#212121' />}
                />
                <Radio
                  initial={true}
                  checked={locationSelected === 'presencial'}
                  disabled={!psychologist.schedules.some(schedule => schedule.location !== null)}
                  label='Presencial'
                  name={psychologist?.id}
                  onClick={() => changeConsultationType('presencial')}
                  icon={<MapPin fontSize={16} color='#212121' />}
                />
              </Row>
            )}
            {locationSelected === 'presencial' && (
              <Row my='10px' justifyContent='space-around' mb='16px'>
                <Select
                  options={psychologist.schedules
                    .filter(schedule => schedule.location !== null)
                    .map(schedule => ({
                      value: schedule.id,
                      label: distance
                        ? `${schedule.prof_address
                            .split(/[\,,\-]/g)[0]
                            .slice(0, 18)} - ${distance} Km`
                        : `${schedule.prof_address.split(/[\,,\-]/g)[0].slice(0, 18)}`
                    }))}
                  onChange={location => setClinicId(location.target.value)}
                  value={clinicId}
                  label='Selecione uma opção'
                  placeholder='Selecione uma opção'
                  name='dependents'
                  variant='secondary'
                />
                <Button
                  variant='primary-outlined'
                  fontSize='12px'
                  height='48px'
                  width='85px'
                  whiteSpace='nowrap'
                  ml='10px'
                  p='0'
                  onClick={() => openMap(psychologist)}
                >
                  Ver mapa
                </Button>
              </Row>
            )}

            <Row width='100%' justifyContent='center'>
              <CaretLeft
                size={24}
                color={previousDay ? '#E7EAF4' : '#FF7708'}
                cursor={previousDay ? 'not-allowed' : 'pointer'}
                onClick={() => !previousDay && handlePreviousWeek()}
              />
              <Calendar
                id='calendar-id'
                className='calendar-class'
                matchCategory={psychologist?.match_category}
                isMatchMaker={false}
                currentSchedule={currentSchedule}
                isSearch={true}
                showWarnings={true}
                isLoading={isCalendarLoading}
                consultationAmount={1}
                schedules={schedules}
                clinicId={clinicId}
                selectedDate={selectedDate}
                setSelectedDate={value => setSelectedDate(value)}
                linkPsychologist={''}
                setIsDisabledLoading={value => setIsCalendarLoading(value)}
                showNotifications={true}
                handleMultipleScheduling={handleMultipleScheduling}
                psychologistInfo={psychologist}
              />
              <CaretRight
                size={24}
                color={lastDay ? '#E7EAF4' : '#FF7708'}
                cursor={lastDay ? 'not-allowed' : 'pointer'}
                onClick={() => !lastDay && handleNextWeek()}
              />
            </Row>
          </Column>
        </Row>
      </Column>
    </Row>
  )
}

export default PsychoCard
